'use client'

import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import Link from 'next/link'

import { faQuestionCircle } from '@document-crunch/pro-regular-svg-icons'

import { useAuth } from '@dc-shared-web/providers/AuthProvider'

import { useGetTeams } from '@dc-shared-web/hooks/api/team'
import { useFSTrackEvent } from '@dc-shared-web/hooks/api/analytic'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'
import { Role } from '@dc-shared-web/hooks/api/user'

import { Input } from '@dc-shared-web/components/shadcn/input'
import { Button } from '@dc-shared-web/components/shadcn/button'

import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import { Tooltip } from '@dc-shared-web/components/base/Tooltip'

import { getUserTeamOptions } from '@dc-shared-web/lib/helpers'
import { cn } from '@dc-shared-web/lib'

import { SearchFilterDialog } from '@/app/search/SearchFilterDialog'

import ProfileSidePane from '@/app/core/components/ProfileSidePane'
import AdminMenu from '@/app/core/components/AdminMenu'
import { useGetSelectedTeamData } from '@/app/core/hooks/useGetSelectedTeamData'
import { getUserRoleForTeam } from '@/app/helpers/userHelper'
import { useLDFlags } from '@/app/core/hooks/useLDFlags'
import NotificationPopoverPanel from '@/app/core/components/notification/NotificationPopoverPanel'
import InnerHeader from '@/app/core/components/InnerHeader'
import { UserProfileMenu } from '@/app/core/components/UserProfileMenu'
const HIDE_SEARCH_PATHS = ['/teams', '/users', '/password', '/crunches']
const INNER_HEADER_PATHS = ['/crunches']
// Button styles in this component are very specific to match the old app.
// The longterm hope would be that we could rely on the Ant Design defaults instead.
export default function Header({
  onClickHelpCenter,
}: {
  onClickHelpCenter?: () => void
}) {
  const { flags, isLoading } = useLDFlags()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { user, isAuthenticating } = useAuth()
  const selectedTeamData = useGetSelectedTeamData(user.teams)
  const userRole = getUserRoleForTeam(user, selectedTeamData.id.toString())
  const { data: teams } = useGetTeams()
  const { trackEvent } = useFSTrackEvent()

  const hideSearch = useMemo(() => {
    let result = false
    for (const path of HIDE_SEARCH_PATHS) {
      if (pathname.includes(path)) {
        result = true
        break
      }
    }
    return result || isAuthenticating
  }, [pathname, isAuthenticating])

  const hideInnerHeader = useMemo(() => {
    let result = true
    for (const path of INNER_HEADER_PATHS) {
      if (pathname.includes(path)) {
        result = false
        break
      }
    }
    return result || isAuthenticating
  }, [pathname, isAuthenticating])

  const trackSearch = (searchVal: string) => {
    trackEvent(FS.SEARCH, {
      search: searchVal || '',
    })
  }

  const searchFilterTeams = user.is_super_admin
    ? teams
    : getUserTeamOptions(user)

  const [searchVal, setSearchVal] = useState('')

  useEffect(() => {
    const searchParam = searchParams.get('search')
    setSearchVal(searchParam ? searchParam : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      trackSearch(searchVal)
      const params = new URLSearchParams(searchParams)
      if (searchVal) {
        params.set('search', searchVal)
      } else {
        params.delete('search')
      }
      router.push(`/search?${params}`)
    }
  }

  const handleInputClear = () => {
    // Only navigate to search if already on search route
    if (pathname.startsWith('/search')) {
      const params = new URLSearchParams(searchParams)
      params.delete('search')
      router.push(`/search?${params}`)
    }

    setSearchVal('')
    trackSearch('')
  }

  const canViewAdminPanel =
    userRole !== Role.MEMBER &&
    userRole !== Role.EDITOR &&
    (user.teams.length || user.is_super_admin)

  const getLogoHref = () => {
    const isAdminPanelPath = pathname === '/teams' || pathname === '/users'
    const teamId = searchParams.get('selectedTeamId')

    if (user.is_super_admin && isAdminPanelPath && teamId) {
      return `/dash?selectedTeamId=${teamId}`
    }

    return `/dash${teamId ? `?selectedTeamId=${teamId}` : ''}`
  }

  const trackingHandler = () => {
    trackEvent(FS.BUTTON_CLICKED, {
      name: 'Help Center',
    })
  }

  const handlerHelpCenter = onClickHelpCenter ?? trackingHandler

  return (
    <div className='tw-py-2 tw-pl-6 tw-pr-5 tw-border-b tw-border-neutral-100 tw-h-14 tw-flex tw-items-center tw-relative tw-bg-white tw-z-50'>
      {!isLoading ? (
        <Link data-testid='logo-link' href={getLogoHref()} prefetch={false}>
          <Image
            src='/nextassets/images/dc-logo-small.svg'
            alt='Document Crunch Logo'
            height={0}
            width={0}
            priority
            style={{ height: 40, width: 50, minWidth: 50 }}
          />
        </Link>
      ) : (
        <div style={{ height: 40, width: 50 }}></div>
      )}
      <div className='tw-ml-3 md:tw-ml-6 tw-flex tw-w-full tw-justify-between tw-items-center tw-gap-2'>
        {!hideSearch && (
          <div className='tw-flex tw-w-[65%] tw-ml-6'>
            <Input
              datatestid='search-input'
              size={'md'}
              value={searchVal}
              clearInputEnabled
              placeholder='Search document or crunched by...'
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                if (!e.target.value) {
                  handleInputClear()
                } else {
                  setSearchVal(e.target.value)
                }
              }}
              onKeyUp={(e: KeyboardEvent<HTMLInputElement>) => handleEnter(e)}
              onClick={() => trackEvent(FS.SEARCH_INPUT_FIELD_CLICKED)}
            />
            <SearchFilterDialog teams={searchFilterTeams || []} />
          </div>
        )}
        {!hideInnerHeader && <InnerHeader />}
        <div
          className={cn(
            'tw-flex tw-gap-2 tw-items-center',
            hideSearch && 'tw-ml-auto'
          )}
        >
          <Tooltip content={'Help Center'} sideOffset={-2} withArrow>
            <Button
              data-testid='help-button'
              variant='ghost'
              onClick={handlerHelpCenter}
              className='tw-w-10'
            >
              <Icon icon={faQuestionCircle} color={IconColor.darkPrimary} />
            </Button>
          </Tooltip>
          {!isAuthenticating && <NotificationPopoverPanel />}
          {/* Unaffiliated User should not see admin dropdown */}
          {canViewAdminPanel && <AdminMenu />}
          {flags.rd108UserProfilePageUpdates ? (
            <UserProfileMenu />
          ) : (
            <ProfileSidePane />
          )}
        </div>
      </div>
    </div>
  )
}
